import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Shield, 
  AlertCircle, 
  CheckCircle,
  Users,
  Globe,
  Lock,
  Mail,
  Phone,
  MapPin,
  BookOpen,
  Scale,
  Clock,
  UserCheck,
  HelpCircle
} from 'lucide-react';
const TermsOfUse = () => {
  const sections = [
    {
      title: "Acceptance of Terms",
      icon: <CheckCircle size={20} />,
      content: "By accessing and using the St. Xavier's Public School website, you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, please do not use our website. We reserve the right to modify these terms at any time without prior notice."
    },
    {
      title: "Use of Website",
      icon: <Globe size={20} />,
      content: "You agree to use the website only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use of the website. Prohibited behavior includes harassing or causing distress to any other user, transmitting obscene or offensive content, or disrupting the normal flow of dialogue within our website."
    },
    {
      title: "Intellectual Property",
      icon: <Shield size={20} />,
      content: "All content on this website, including text, graphics, logos, images, audio clips, digital downloads, and software, is the property of St. Xavier's Public School or its content suppliers and is protected by copyright laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission."
    },
    {
      title: "User Accounts",
      icon: <Users size={20} />,
      content: "To access certain features of our website, you may be required to create an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account."
    },
    {
      title: "Privacy Policy",
      icon: <Lock size={20} />,
      content: "Your use of the website is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding the collection and use of your personal information."
    },
    {
      title: "Prohibited Activities",
      icon: <AlertCircle size={20} />,
      content: "You may not use the website for any purpose that is unlawful or prohibited by these terms. Prohibited activities include:",
      points: [
        "Attempting to interfere with the website's functionality",
        "Uploading viruses or malicious code",
        "Collecting user data without consent",
        "Impersonating another person or entity",
        "Engaging in any automated use of the system"
      ]
    },
    {
      title: "Third-Party Links",
      icon: <Globe size={20} />,
      content: "Our website may contain links to third-party websites. These links are provided for your convenience only. We do not control or endorse these websites and are not responsible for their content or practices. Your use of third-party websites is at your own risk."
    },
    {
      title: "Disclaimer of Warranties",
      icon: <Scale size={20} />,
      content: "The website and its content are provided 'as is' without any warranties, express or implied. We do not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components. Your use of the website is at your sole risk."
    },
    {
      title: "Limitation of Liability",
      icon: <AlertCircle size={20} />,
      content: "To the fullest extent permitted by law, St. Xavier's Public School shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the website, even if we have been advised of the possibility of such damages."
    },
    {
      title: "Indemnification",
      icon: <Shield size={20} />,
      content: "You agree to indemnify, defend, and hold harmless St. Xavier's Public School, its officers, directors, employees, agents, and affiliates from any claims, damages, losses, liabilities, costs, or expenses arising from your use of the website or violation of these terms."
    },
    {
      title: "Termination",
      icon: <Clock size={20} />,
      content: "We reserve the right to terminate or suspend your access to all or part of the website without notice, for any reason, including violation of these terms. Upon termination, your right to use the website will immediately cease."
    },
    {
      title: "Governing Law",
      icon: <Scale size={20} />,
      content: "These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts in Varanasi, Uttar Pradesh."
    }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block"
          >
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
              <FileText size={16} />
              Legal Agreement
            </span>
          </motion.div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-5 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
            Terms of Use
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-red-500 to-blue-600 mx-auto mt-3 rounded-full"></div>
          <p className="text-gray-500 mt-3 text-sm">Last Updated: January 1, 2024</p>
          <p className="text-gray-500 text-sm">St. Xavier's Public School, Bela Varanasi</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-blue-500"
        >
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 mb-1">Welcome to St. Xavier's Public School</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                These Terms of Use govern your access to and use of the St. Xavier's Public School website, 
                including any content, functionality, and services offered through our website. 
                Please read these terms carefully before using our website.
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6"
        >
          <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <HelpCircle size={18} className="text-blue-600" />
            Contact Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-gray-400" />
              <span className="text-gray-600">Bela Cholapur Varanasi - 221101, UP</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-gray-400" />
              <span className="text-gray-600">+91 8601017877, +91 9450785369</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-gray-400" />
              <span className="text-gray-600">st.xavierbelavns@gmail.com</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-gray-400" />
              <span className="text-gray-600">www.stxaviersbela.com</span>
            </div>
          </div>
        </motion.div>
        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.03 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="border-b border-gray-100">
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-white">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <div className="text-blue-600">{section.icon}</div>
                  </div>
                  <h2 className="font-bold text-gray-800 text-lg">{section.title}</h2>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-600 text-sm leading-relaxed">
                  {section.content}
                </p>
                {section.points && (
                  <ul className="space-y-2 mt-3">
                    {section.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-md p-5 mt-6 border border-gray-100"
        >
          <div className="flex items-start gap-3">
            <div className="bg-green-100 p-2 rounded-full">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-1">Acknowledgment</h3>
              <p className="text-sm text-gray-600">
                By using our website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. 
                If you do not agree to these terms, please do not use our website. Your continued use of the website following 
                any changes to these terms constitutes acceptance of those changes.
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.65 }}
          className="mt-8 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
            <Scale className="w-10 h-10 mx-auto mb-3 opacity-80" />
            <h3 className="text-xl font-bold mb-2">Questions About Terms?</h3>
            <p className="text-blue-100 text-sm">
              If you have any questions about these Terms of Use, please contact us.
              We're here to help clarify any concerns you may have.
            </p>
            <div className="mt-4 flex justify-center gap-4 text-sm">
              <a href="/privacy-policy" className="text-yellow-300 hover:text-yellow-200 transition">Privacy Policy</a>
              <span>•</span>
              <a href="/contact" className="text-yellow-300 hover:text-yellow-200 transition">Contact Us</a>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default TermsOfUse;